import {
  Room,
  RoomEvent,
  ParticipantKind,
  Track,
  MediaDeviceFailure,
  createAudioAnalyser,
} from "livekit-client";
import type { RemoteTrack, RemoteParticipant, Participant, LocalAudioTrack, RemoteAudioTrack } from "livekit-client";
import type { StoreApi } from "zustand/vanilla";
import {
  applyDataChannelMessage,
  createEffectRouter,
  normalizeTranscriptionSegments,
  micErrorFromFailure,
  isDataChannelMessage,
  encodeEvent,
  scanSections,
} from "@moss-js/founding-agent/core";
import type {
  VoiceAgentStore,
  BookingPublisher,
  EffectRouterDeps,
} from "@moss-js/founding-agent/core";

export interface IntakePublisher {
  publishIntakeOpened(): void;
  publishIntakeCompleted(ev: { responses: Record<string, string> }): void;
}

export interface LiveKitTransport {
  connect(token: string, serverUrl: string): Promise<void>;
  disconnect(): void;
  setMuted(muted: boolean): void;
  bookingPublisher: BookingPublisher;
  intakePublisher: IntakePublisher;
  publishPageContext(): void;
  _handleData(bytes: Uint8Array): void;
  _shouldPlay(participant: { kind: string | number; identity: string }): boolean;
  setInteractive(interactive: boolean): void;
  destroy(): void;
}

export function createTransport(
  store: StoreApi<VoiceAgentStore>,
  opts: { effects: EffectRouterDeps },
): LiveKitTransport {
  const room = new Room();
  let roomConnected = false;
  let wantConnected = false;
  // Wrap navigate so agent-driven navigation republishes page context once the
  // route settles (SPA onNavigate routers pushState, which fires no popstate).
  let effectDeps: EffectRouterDeps = {
    ...opts.effects,
    navigate: (path) => {
      opts.effects.navigate(path);
      schedulePageContextPublish();
    },
  };
  let effectRouter = createEffectRouter(effectDeps);

  function _handleData(bytes: Uint8Array): void {
    const raw = new TextDecoder().decode(bytes);
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    if (effectRouter.handle(parsed)) return;
    if (isDataChannelMessage(parsed)) {
      applyDataChannelMessage(store, parsed);
    }
  }

  function _shouldPlay(_participant: { kind: string | number; identity: string }): boolean {
    return true;
  }

  // Track audio elements keyed by track sid
  const audioElements = new Map<string, HTMLAudioElement>();

  // Audio analyser handles keyed by track sid; cleanup is async
  interface AnalyserEntry {
    cleanup: () => Promise<void>;
    intervalId: ReturnType<typeof setInterval>;
  }
  const agentAnalysers = new Map<string, AnalyserEntry>();
  let localAnalyserEntry: AnalyserEntry | null = null;

  const LEVEL_HZ = 25;

  function startAgentAnalyser(track: RemoteAudioTrack, sid: string): void {
    if (agentAnalysers.has(sid)) return;
    try {
      const analyser = createAudioAnalyser(track, { fftSize: 256 });
      const intervalId = setInterval(() => {
        const v = Math.min(1, Math.max(0, analyser.calculateVolume()));
        store.getState().setAgentAudioLevel(v);
      }, Math.round(1000 / LEVEL_HZ));
      agentAnalysers.set(sid, { cleanup: analyser.cleanup, intervalId });
    } catch {
      // createAudioAnalyser may fail in test env; silently skip
    }
  }

  function stopAgentAnalyser(sid: string): void {
    const entry = agentAnalysers.get(sid);
    if (!entry) return;
    clearInterval(entry.intervalId);
    void entry.cleanup();
    agentAnalysers.delete(sid);
    store.getState().setAgentAudioLevel(0);
  }

  function startLocalAnalyser(track: LocalAudioTrack): void {
    if (localAnalyserEntry) return;
    try {
      const analyser = createAudioAnalyser(track, { fftSize: 256 });
      const intervalId = setInterval(() => {
        const v = Math.min(1, Math.max(0, analyser.calculateVolume()));
        store.getState().setUserMicVolume(v);
      }, Math.round(1000 / LEVEL_HZ));
      localAnalyserEntry = { cleanup: analyser.cleanup, intervalId };
    } catch {
      // createAudioAnalyser may fail in test env; silently skip
    }
  }

  function stopLocalAnalyser(): void {
    if (!localAnalyserEntry) return;
    clearInterval(localAnalyserEntry.intervalId);
    void localAnalyserEntry.cleanup();
    localAnalyserEntry = null;
    store.getState().setUserMicVolume(0);
  }

  function attachAudio(track: RemoteTrack, participant: RemoteParticipant): void {
    if (track.kind !== Track.Kind.Audio) return;
    if (!_shouldPlay(participant)) return;
    if (!track.sid) return;
    const el = document.createElement("audio");
    el.autoplay = true;
    el.style.cssText = "position:absolute;width:0;height:0;opacity:0;pointer-events:none;";
    (track as any).attach(el);
    document.body.appendChild(el);
    audioElements.set(track.sid, el);
    // Wire agent audio level if this is the agent participant
    const isAgent =
      (participant as any).kind === ParticipantKind.AGENT ||
      (participant as any).kind === "agent";
    if (isAgent) {
      startAgentAnalyser(track as unknown as RemoteAudioTrack, track.sid);
    }
  }

  function detachAudio(track: RemoteTrack): void {
    if (!track.sid) return;
    const el = audioElements.get(track.sid);
    if (!el) return;
    (track as any).detach(el);
    el.remove();
    audioElements.delete(track.sid);
    stopAgentAnalyser(track.sid);
  }

  room.on(RoomEvent.DataReceived, (payload: Uint8Array) => {
    _handleData(payload);
  });

  // Accumulate transcription segments by id across events. RoomEvent.Transcription-
  // Received delivers only the current event's segments; the React path uses
  // useTranscriptions() (which retains the full history), so we mirror that here.
  // Otherwise each event would replace the store and drop earlier turns.
  const transcriptionsById = new Map<string, {
    streamInfo: { id: string; timestamp: number; attributes: Record<string, string> };
    participantInfo: { identity: string };
    text: string;
  }>();
  room.on(RoomEvent.TranscriptionReceived, (segments, participant?: Participant) => {
    const localIdentity = room.localParticipant?.identity ?? "";
    for (const seg of segments) {
      transcriptionsById.set(seg.id, {
        streamInfo: {
          id: seg.id,
          timestamp: seg.startTime,
          attributes: { "lk.transcription_final": seg.final ? "true" : "false" },
        },
        participantInfo: { identity: participant?.identity ?? "" },
        text: seg.text,
      });
    }
    store.getState().setStreamingTranscripts(
      normalizeTranscriptionSegments([...transcriptionsById.values()], localIdentity),
    );
  });

  room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _publication, participant: RemoteParticipant) => {
    attachAudio(track, participant);
  });

  room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
    detachAudio(track);
  });

  room.on(RoomEvent.MediaDevicesError, (error: Error, kind?: MediaDeviceKind) => {
    const failure = MediaDeviceFailure.getFailure(error);
    const micErr = micErrorFromFailure(failure, kind);
    if (micErr !== null) {
      store.getState().setMicError(micErr);
    }
  });

  room.on(RoomEvent.Connected, () => {
    if (!wantConnected) { room.disconnect(); return; }
    roomConnected = true;
    store.getState().setConnectionState("connected");
    // Wire up chat: allow the store to send chat messages via the room
    store.getState().setSendChatMessage((msg: string) => {
      store.getState().addChatPrompt(msg);
      void room.localParticipant.sendText(msg, { topic: "lk.chat" }).catch(() => { /* chat send is best-effort */ });
    });
    // Start local mic analyser
    const micPub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
    const micTrack = micPub?.track;
    if (micTrack && micTrack.kind === Track.Kind.Audio) {
      startLocalAnalyser(micTrack as unknown as LocalAudioTrack);
    }
  });

  room.on(RoomEvent.Disconnected, () => {
    roomConnected = false;
    store.getState().setConnectionState("idle");
    store.getState().setSendChatMessage(null);
    cleanupRoomResources();
  });

  room.on(RoomEvent.ParticipantAttributesChanged, (changedAttributes: Record<string, string>, participant: Participant) => {
    if ("lk.agent.state" in changedAttributes) {
      const val = changedAttributes["lk.agent.state"] as any;
      store.getState().setAgentVoiceState(val);
      const VALID = ["initializing", "listening", "thinking", "speaking", "idle"];
      if (typeof val === "string" && VALID.includes(val)) {
        store.getState().setAgentDisplayState(val as any);
      }
    }
  });

  const bookingPublisher: BookingPublisher = {
    publishBookingCompleted(ev) {
      const encoded = encodeEvent({ type: "booking_completed", ...ev });
      void room.localParticipant.publishData(encoded, { reliable: true }).catch(() => { /* data publish is best-effort */ });
    },
    publishBookingFailed(ev) {
      const encoded = encodeEvent({ type: "booking_failed", reason: ev.reason });
      void room.localParticipant.publishData(encoded, { reliable: true }).catch(() => { /* data publish is best-effort */ });
    },
  };

  const intakePublisher: IntakePublisher = {
    publishIntakeOpened() {
      const encoded = encodeEvent({ type: "intake_opened" });
      void room.localParticipant.publishData(encoded, { reliable: true }).catch(() => { /* data publish is best-effort */ });
    },
    publishIntakeCompleted(ev) {
      const encoded = encodeEvent({ type: "intake_completed", responses: ev.responses });
      void room.localParticipant.publishData(encoded, { reliable: true }).catch(() => { /* data publish is best-effort */ });
    },
  };

  function publishPageContext(): void {
    // Never mutate the host page's DOM: nav targets come from data-voice-section
    // annotations and elements that already have ids.
    const pageEv = scanSections(document, window.location.pathname, { generateHeadingIds: false });
    const encoded = encodeEvent(pageEv);
    void room.localParticipant.publishData(encoded, { reliable: true }).catch(() => { /* page-context publish is best-effort */ });
  }

  // Republish page context after route changes so the agent doesn't keep an
  // SPA's stale path/section list. Mirrors the React bridge's PageContextEmitter:
  // publish once, 250ms after navigation settles, only while connected and
  // interactive. popstate/hashchange cover host-router and back/forward
  // navigation; the navigate wrapper above covers agent-driven navigation.
  const PAGE_PUBLISH_SETTLE_MS = 250;
  let pagePublishTimer: ReturnType<typeof setTimeout> | null = null;
  function schedulePageContextPublish(): void {
    if (pagePublishTimer !== null) clearTimeout(pagePublishTimer);
    pagePublishTimer = setTimeout(() => {
      pagePublishTimer = null;
      if (!roomConnected || !effectDeps.interactive) return;
      publishPageContext();
    }, PAGE_PUBLISH_SETTLE_MS);
  }

  const onRouteChange = (): void => {
    if (!roomConnected || !effectDeps.interactive) return;
    schedulePageContextPublish();
  };
  if (typeof window !== "undefined") {
    window.addEventListener("popstate", onRouteChange);
    window.addEventListener("hashchange", onRouteChange);
  }

  async function connect(token: string, serverUrl: string): Promise<void> {
    wantConnected = true;
    store.getState().setConnectionState("connecting");
    try {
      await room.connect(serverUrl, token, { autoSubscribe: true });
    } catch (err) {
      store.getState().setConnectionState("error");
      throw err; // room-level failure: nothing connected, let open() reset
    }
    if (!wantConnected) return; // cancelled during connect: do not request the mic
    // Room is connected. Respect a pre-open mute (setMuted before open records
    // isMicMuted without a live room). Mic is best-effort: on failure keep the
    // call alive and let the mic-error overlay drive retry.
    try {
      await room.localParticipant.setMicrophoneEnabled(!store.getState().isMicMuted);
    } catch (err) {
      const failure = MediaDeviceFailure.getFailure(err as Error);
      const micErr = micErrorFromFailure(failure, "audioinput");
      if (micErr) store.getState().setMicError(micErr);
    }
  }

  // Stop analysers and tear down attached audio elements. Idempotent and
  // synchronous, so it can run both from the Disconnected handler and from
  // destroy() (which strips listeners and can't rely on Disconnected firing).
  function cleanupRoomResources(): void {
    if (pagePublishTimer !== null) {
      clearTimeout(pagePublishTimer);
      pagePublishTimer = null;
    }
    stopLocalAnalyser();
    for (const sid of [...agentAnalysers.keys()]) {
      stopAgentAnalyser(sid);
    }
    for (const el of audioElements.values()) {
      el.remove();
    }
    audioElements.clear();
    transcriptionsById.clear();
  }

  function disconnect(): void {
    wantConnected = false;
    cleanupRoomResources();
    room.disconnect();
  }

  // Last isMicMuted value applied to the device (or recorded as pre-open
  // intent). Lets the store subscription below tell a store-driven mute
  // (e.g. openIntake()/resolveIntake()) apart from one setMuted() already
  // applied, so the device is never toggled twice for the same change.
  let lastAppliedMuted = store.getState().isMicMuted;

  function applyMicMuted(muted: boolean): void {
    lastAppliedMuted = muted;
    // Only touch the device when actually connected AND still wanted.
    // disconnect() flips wantConnected synchronously but roomConnected only
    // flips later, off the async RoomEvent.Disconnected -- so a reset() run
    // right after disconnect() (see end()) lands here while roomConnected is
    // still stale-true. Without the wantConnected check that races a device
    // call onto a room mid-teardown. A pre-open mute is applied by connect()
    // reading isMicMuted, so skipping here is safe either way.
    if (!roomConnected || !wantConnected) return;
    // Catch: unmuting can fail if permission/device state changed since
    // connect. Surface it as a mic error rather than an unhandled rejection.
    void room.localParticipant.setMicrophoneEnabled(!muted).catch((err) => {
      const micErr = micErrorFromFailure(MediaDeviceFailure.getFailure(err as Error), "audioinput");
      if (micErr) store.getState().setMicError(micErr);
    });
  }

  function setMuted(muted: boolean): void {
    // Mark this change as already applied before writing the store: the
    // mirroring subscription below fires synchronously off this set() call
    // and must treat it as already handled, or the device gets toggled twice.
    lastAppliedMuted = muted;
    store.getState().setMicMuted(muted);
    applyMicMuted(muted);
  }

  // Mirror store-driven isMicMuted changes (e.g. the intake trigger's
  // openIntake()/resolveIntake(), which write isMicMuted directly) onto the
  // LiveKit mic, exactly like the React bridge's MicMuteController effect.
  // setMuted() above already applies its own change via applyMicMuted, so
  // lastAppliedMuted lets this subscription skip that no-op transition.
  let prevIsMicMuted = store.getState().isMicMuted;
  const micMutedUnsub = store.subscribe((state) => {
    if (state.isMicMuted === prevIsMicMuted) return;
    prevIsMicMuted = state.isMicMuted;
    if (state.isMicMuted === lastAppliedMuted) return;
    applyMicMuted(state.isMicMuted);
  });

  function setInteractive(interactive: boolean): void {
    effectDeps = { ...effectDeps, interactive };
    effectRouter = createEffectRouter(effectDeps);
  }

  // Close the mic-retry loop: the mic-error overlay's "Try again" bumps
  // micRetryNonce (and sets micRetryInFlight); re-request the device here and
  // clear the in-flight flag so the button works and can be pressed again.
  let prevMicRetryNonce = store.getState().micRetryNonce;
  const micRetryUnsub = store.subscribe((state) => {
    if (state.micRetryNonce !== prevMicRetryNonce) {
      prevMicRetryNonce = state.micRetryNonce;
      void retryMic();
    }
  });

  async function retryMic(): Promise<void> {
    try {
      if (!roomConnected) return;
      await room.localParticipant.setMicrophoneEnabled(true);
      const micPub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
      const micTrack = micPub?.track;
      if (micTrack && micTrack.kind === Track.Kind.Audio) {
        startLocalAnalyser(micTrack as unknown as LocalAudioTrack);
      }
      store.getState().setMicError(null);
    } catch (err) {
      // Retry failed. Set the error explicitly (not only via MediaDevicesError,
      // which may not fire on a direct promise rejection) so the overlay stays.
      const failure = MediaDeviceFailure.getFailure(err as Error);
      const micErr = micErrorFromFailure(failure, "audioinput");
      store.getState().setMicError(micErr ?? "other");
    } finally {
      store.getState().setMicRetryInFlight(false);
    }
  }

  function destroy(): void {
    wantConnected = false;
    if (typeof window !== "undefined") {
      window.removeEventListener("popstate", onRouteChange);
      window.removeEventListener("hashchange", onRouteChange);
    }
    micRetryUnsub();
    micMutedUnsub();
    room.disconnect();
    // Clean up synchronously before stripping listeners: once removeAllListeners
    // runs, a later Disconnected event can't fire our audio-cleanup handler.
    cleanupRoomResources();
    room.removeAllListeners();
  }

  return {
    connect,
    disconnect,
    setMuted,
    bookingPublisher,
    intakePublisher,
    publishPageContext,
    _handleData,
    _shouldPlay,
    setInteractive,
    destroy,
  };
}
